import type { SubscriptionTier } from "./tierAccess";

export interface WidgetDef {
  id: string;
  label: string;
  description: string;
  category: string;
  defaultPinned: boolean;
  /**
   * Minimum subscription tier required to see + use this widget. The
   * dashboard picker filters on this, and the hook filters it out of
   * the render path for lower-tier users.
   *
   * Most existing widgets are Choice because they mirror content gated
   * behind Choice-only routes (market-report, market-pulse, regional-
   * markets, supply-demand, freight, profitability, fed-cattle tools,
   * news intelligence). A Select user's `/dashboard` should only offer
   * widgets whose source tools they can actually reach from the sidebar.
   */
  tier: SubscriptionTier;
}

export const WIDGET_REGISTRY: WidgetDef[] = [
  // ── Alerts & Intelligence ──
  // Source tools live under `/news/*` + `/market-report` — all Choice.
  { id: "weekly-market-brief", label: "Weekly Market Brief", description: "AI-generated weekly summary of feeder cattle market conditions and key trends.", category: "Alerts & Intelligence", defaultPinned: true, tier: "choice" },
  { id: "market-spotlight", label: "Market Spotlight", description: "Breaking market alerts triggered by unusual price moves, volume shifts, or supply signals.", category: "Alerts & Intelligence", defaultPinned: true, tier: "choice" },
  { id: "market-report", label: "Market Report", description: "National steer prices by weight class with WoW change + producer sentiment vote. Links to full AI commentary.", category: "Alerts & Intelligence", defaultPinned: true, tier: "choice" },
  // Source tool /market-calendar is Select — the only Select-tier widget, so a
  // Select user's dashboard offers at least one card they can fully drill into.
  { id: "next-report", label: "Next Report", description: "Countdown to the next market-moving USDA reports (Cattle on Feed, COT, Cold Storage…). Links to the full Market Report Calendar.", category: "Alerts & Intelligence", defaultPinned: true, tier: "select" },

  // ── Market Overview (previously fixed dashboard sections) ──
  // Composite/aggregate market widgets — the underlying data surfaces
  // live on `/market-pulse` / `/dv-auction` / `/supply-demand` / `/freight`,
  // all Choice. Not meaningful without those Choice tools for drill-down.
  { id: "market-index", label: "Market Indices", description: "Composite market health indicators across cash, futures, and supply metrics.", category: "Market Overview", defaultPinned: true, tier: "choice" },
  // "market-snapshot" / Cash Feeder Market was removed on 2026-04-14 after a
  // 10/10 audit found the single blended "national today" number was
  // methodologically unsound (blends Steers + Heifers + Cows across all
  // weights, compares today's barn set against a different prior-day barn
  // set, capped at 200 DV rows). DVAuctionTicker + DVAuctionDetailView serve
  // the real JTBD with weight-class × cattle-type separation and weekly
  // comparison that actually works. See audit in docs / commit history.
  { id: "market-strength", label: "Cash Feeder Price Trend", description: "30-day rolling cash feeder price trend with directional momentum signal.", category: "Market Overview", defaultPinned: true, tier: "choice" },
  { id: "dv-auction-ticker", label: "USDA Market Ticker", description: "Latest USDA livestock market report headlines and price summaries.", category: "Market Overview", defaultPinned: true, tier: "choice" },
  { id: "cme-settlements", label: "CME Daily Settlements", description: "End-of-day settlement prices for feeder cattle, live cattle, and corn futures.", category: "Market Overview", defaultPinned: true, tier: "choice" },
  { id: "diesel-banner", label: "Diesel Price Banner", description: "National diesel price with week-over-week change — a proxy for freight cost trends.", category: "Market Overview", defaultPinned: true, tier: "choice" },

  // ── Live Market Data (from the Live Market Dashboard) ──
  // Mirror content from `/live` (ClaraLiveHome — Select) and downstream
  // Choice-tier tools. The aggregate views themselves are packer- / fed-
  // cattle-focused, which are Choice-gated in the sidebar.
  { id: "market-pulse", label: "Market Pulse", description: "Real-time vital signs: cash trade health, packer margins, slaughter pace, negotiated share.", category: "Live Market Data", defaultPinned: false, tier: "choice" },
  { id: "price-spread", label: "National Price Spread", description: "Geographic price dispersion across states — highlights regional arbitrage opportunities.", category: "Live Market Data", defaultPinned: false, tier: "choice" },
  { id: "live-trade-feed", label: "Live Trade Feed", description: "Real-time DV Auction transaction feed showing individual lot sales as they report.", category: "Live Market Data", defaultPinned: false, tier: "choice" },
  { id: "cattle-report", label: "Cattle Report Panel", description: "CME cash cattle and futures summary in a compact panel format.", category: "Live Market Data", defaultPinned: false, tier: "choice" },
  { id: "boxed-beef", label: "Boxed Beef Primals", description: "USDA boxed beef cutout values and primal breakdown — downstream demand signal.", category: "Live Market Data", defaultPinned: false, tier: "choice" },
  { id: "cme-futures-curve", label: "CME Futures Curve", description: "Forward futures curve for feeder cattle, live cattle, and corn contracts.", category: "Live Market Data", defaultPinned: false, tier: "choice" },

  // ── Supply & Demand ──
  // Source tool `/supply-demand` — Choice.
  { id: "supply-pressure", label: "Regional Supply Index", description: "Auction receipts trend by region — rising volume signals supply pressure on prices.", category: "Supply & Demand", defaultPinned: true, tier: "choice" },
  { id: "weight-demand-heatmap", label: "Weight Class Demand", description: "Heatmap showing which weight classes are seeing the strongest buyer demand.", category: "Supply & Demand", defaultPinned: true, tier: "choice" },
  { id: "weight-slide", label: "Weight Slide Analyzer", description: "Price differentials between consecutive weight classes — reveals the cost of gain at auction.", category: "Supply & Demand", defaultPinned: false, tier: "choice" },

  // ── Regional Markets ──
  // Source tool `/regional-markets` — Choice.
  { id: "price-map", label: "Regional Price Map", description: "Color-coded US map showing feeder cattle prices by state relative to the national average.", category: "Regional Markets", defaultPinned: true, tier: "choice" },
  { id: "best-sale-scanner", label: "Best Sale Scanner", description: "Ranks sale barns by premium vs national cohort — find where cattle are selling cheapest.", category: "Regional Markets", defaultPinned: false, tier: "choice" },

  // ── Freight Intelligence ──
  // Source tool `/freight` — Choice.
  { id: "where-to-buy", label: "Where Should I Buy?", description: "Ranks barns by lowest delivered cost to your feedlot, combining auction price + freight.", category: "Freight", defaultPinned: false, tier: "choice" },
  { id: "freight-calc", label: "Freight Impact Calculator", description: "Calculate freight cost per head and delivered price for any origin-destination pair.", category: "Freight", defaultPinned: false, tier: "choice" },
  { id: "freight-volatility", label: "Diesel Price Trends", description: "Historical diesel price chart with 7/30/90-day trend lines for freight cost planning.", category: "Freight", defaultPinned: false, tier: "choice" },

  // ── Profitability ──
  // Source tool `/profitability` — Choice.
  { id: "sell-decision", label: "Should I Sell?", description: "Live sell/hold/wait decision tool using DV Auction cash prices + CME futures + breakeven.", category: "Profitability", defaultPinned: true, tier: "choice" },
  { id: "placement-margin", label: "Placement Margin Indicator", description: "Industry placement profitability by weight class with K-State cost model + ROI classification.", category: "Profitability", defaultPinned: true, tier: "choice" },
  // feeder-forecast removed — breakeven + sensitivity folded into placement-margin
];

export const WIDGET_MAP = Object.fromEntries(WIDGET_REGISTRY.map(w => [w.id, w]));

/**
 * Given a list of widget IDs and the current user's tier, return only the
 * IDs the user has access to. Used by the Dashboard grid to render the
 * right widgets and by the Customize dialog to populate the picker.
 *
 * IDs not present in the registry are filtered out (dead / retired widget
 * ids stored from a prior version of the schema).
 *
 * When `userTier` is null (auth not yet loaded), returns an empty list
 * — safer to render nothing for a frame than to briefly render Choice
 * widgets to a Select user.
 */
import { canAccessTier } from "./tierAccess";

export function filterWidgetIdsByTier(
  ids: string[],
  userTier: SubscriptionTier | null,
): string[] {
  if (!userTier) return [];
  return ids.filter((id) => {
    const w = WIDGET_MAP[id];
    if (!w) return false;
    return canAccessTier(userTier, w.tier);
  });
}

export function filterWidgetsByTier(
  widgets: WidgetDef[],
  userTier: SubscriptionTier | null,
): WidgetDef[] {
  if (!userTier) return [];
  return widgets.filter((w) => canAccessTier(userTier, w.tier));
}
